import { useCallback, useEffect, useState } from 'react';

import {
    useAddToWishlist,
    useAppDispatch,
    useCreateWishlist,
    useDeleteFromWishlist,
    useItemInWishlists,
    useWishlistData,
} from '@pkg/hooks';

import { type CollectionChainType } from '@app/client';
import { type IWishlist } from '@app/client/common/structs/wishlists/wishlists';
import { useCollectionContracts } from '@app/pages/Collection/hooks';
import { countFormatter } from '@app/pages/Wishlist/helpers';
import { wishlistActions } from '@app/store/slices';

interface Props {
    contract?: string;
    image?: string;
    name?: string;
    onError?: VoidFunction;
    onClicked?: VoidFunction;
    onSuccess?: VoidFunction;
    chain: CollectionChainType;
    collectionSlug?: string;
    tokenId?: string;
}

export const useHandleWishlist = ({ contract, onError, onSuccess, collectionSlug, tokenId, chain }: Props) => {
    const { data: collectionContracts } = useCollectionContracts({ slug: contract ? '' : collectionSlug });
    const { mutateAsync: addToWishlist } = useAddToWishlist();
    const { mutateAsync: deleteFromWishlist } = useDeleteFromWishlist();
    const { data: wishlistData } = useWishlistData();
    const [isAddedItem, setIsAddedItem] = useState(false);

    const dispatch = useAppDispatch();

    const {
        data: itemInWishlistsData,
        refetch,
        isLoading,
    } = useItemInWishlists({
        contract: contract || collectionContracts?.contracts[0],
        token_id: tokenId,
        slug: collectionSlug,
        chain,
    });

    const wishlists = itemInWishlistsData?.[0]?.wishlists?.filter((list) => list.wishlist_type !== 'Fantasy') || [];
    const addedItem = Boolean(wishlists?.length);
    const wishlistId = wishlists?.[0]?.slug;
    const wishlistName = wishlists?.[0]?.name;
    const wishlistCount = itemInWishlistsData?.[0]?.counts?.user_count;
    const wishlistedCount = countFormatter(wishlistCount);

    useEffect(() => {
        setIsAddedItem(addedItem);
    }, [addedItem]);

    const hasAssetData = Boolean(contract && tokenId);
    const hasCollectionData = Boolean(collectionSlug);

    const hasData = hasCollectionData || hasAssetData;

    const { mutateAsync: createWishlist } = useCreateWishlist();

    const handleAddToWishlist = useCallback(
        (mostRecentWishlist: IWishlist) => {
            if (wishlistData && hasData) {
                addToWishlist({
                    wishlistId: mostRecentWishlist.slug,
                    contract,
                    token_id: tokenId,
                    slug: collectionSlug,
                    network: chain,
                })
                    .then((r) => {
                        if (r?.ok) {
                            dispatch(wishlistActions.setWishListToastSlug(mostRecentWishlist.slug));
                            dispatch(wishlistActions.setWishListToastName(mostRecentWishlist.name));
                            refetch();
                            setIsAddedItem(true);
                            onSuccess?.();
                        } else {
                            onError?.();
                        }
                    })
                    .catch(() => {
                        onError?.();
                        refetch();
                    });
            }
        },
        [
            wishlistData,
            hasData,
            addToWishlist,
            contract,
            tokenId,
            collectionSlug,
            dispatch,
            refetch,
            onSuccess,
            onError,
            chain,
        ],
    );

    const handleCreateWishlist = useCallback(() => {
        createWishlist({ name: 'My wishlist', isPrivate: false }).then((newWishlist) => {
            if (newWishlist?.ok && newWishlist.value) {
                handleAddToWishlist(newWishlist.value);
                onSuccess && onSuccess();
            }
        });
    }, [createWishlist, handleAddToWishlist, onSuccess]);

    const handleRemoveFromWishlist = useCallback(() => {
        if (wishlistData && hasData && wishlistId && wishlistName) {
            deleteFromWishlist({
                wishlistId,
                contract,
                token_id: tokenId,
                slug: collectionSlug,
                network: chain,
            })
                .then(() => {
                    dispatch(wishlistActions.setRemoveWishListToastName(wishlistName));
                    dispatch(wishlistActions.setRemoveWishListToastSlug(wishlistId));
                    dispatch(
                        wishlistActions.setUndoWishlistData({
                            wishlistSlug: wishlistId,
                            contract,
                            tokenId,
                            collectionSlug,
                            chain,
                        }),
                    );

                    setIsAddedItem(false);
                    onSuccess && onSuccess();
                    refetch();
                })
                .catch(() => {
                    onError?.();
                    refetch();
                });
        }
    }, [
        wishlistData,
        hasData,
        wishlistId,
        wishlistName,
        deleteFromWishlist,
        contract,
        tokenId,
        collectionSlug,
        chain,
        dispatch,
        onSuccess,
        refetch,
        onError,
    ]);

    const handleAddFlow = useCallback(() => {
        if (wishlistData && hasData) {
            const mostRecentWishlist = wishlistData.pages?.[0]?.data[0];
            if (mostRecentWishlist) handleAddToWishlist(mostRecentWishlist);
            else handleCreateWishlist();
        }
    }, [handleAddToWishlist, wishlistData, hasData, handleCreateWishlist]);

    return {
        handleAddToWishlist,
        handleCreateWishlist,
        handleAddFlow,
        handleRemoveFromWishlist,
        isAddedItem,
        isLoading,
        wishlistedCount,
    };
};
